import "@schedule-x/theme-default/dist/index.css";
import "temporal-polyfill/global";

import {
  MachinePublicHydrated,
  Schedule,
} from "@aura-frontend/omnibus-az-client";
import { createViewWeek } from "@schedule-x/calendar";
import { createEventModalPlugin } from "@schedule-x/event-modal";
import { ScheduleXCalendar, useCalendarApp } from "@schedule-x/react";
import { cn, Skeleton } from "@win-reality/react-ui";
import { memo, useEffect, useState } from "react";

import { getTeamNames } from "@/utils/eventUtils";

import { UpcomingEventItem } from "../UpcomingEventItem";
import { useCalendarEvents } from "./FieldCalendar.queries";

const DEFAULT_DAY_START_HOUR = 6;
const DEFAULT_DAY_END_HOUR = 23;

function computeStretchedBoundaryPoints(schedules: Schedule[]) {
  let minStartHour = DEFAULT_DAY_START_HOUR;
  let maxEndCeilHour = DEFAULT_DAY_END_HOUR;

  for (const schedule of schedules) {
    const game = schedule.merged?.game;
    if (!game) continue;

    const startDate = new Date(schedule.schedule_time_s * 1000);
    if (startDate.getHours() < minStartHour) {
      minStartHour = startDate.getHours();
    }

    const lastTime = game.times.at(-1) as
      | { end_time?: number; endTime?: number }
      | undefined;
    const endTimeSec = lastTime?.endTime ?? lastTime?.end_time;
    if (!endTimeSec) continue;
    const endDate = new Date(Number(endTimeSec) * 1000);

    // Hours of end-of-event measured from the start day's midnight, so events
    // crossing midnight push the boundary toward 24:00 instead of wrapping.
    const startDayMidnight = new Date(startDate);
    startDayMidnight.setHours(0, 0, 0, 0);
    const hoursFromStartDay =
      (endDate.getTime() - startDayMidnight.getTime()) / 3_600_000;
    const ceilHour = Math.min(24, Math.ceil(hoursFromStartDay));
    if (ceilHour > maxEndCeilHour) maxEndCeilHour = ceilHour;
  }

  return { start: Math.max(0, minStartHour) * 100, end: maxEndCeilHour * 100 };
}

function toCalendarEvents(schedules: Schedule[]) {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return schedules.flatMap((schedule, i) => {
    const game = schedule.merged?.game;
    if (!game) return [];

    const { homeTeamName, awayTeamName } = getTeamNames(game);
    const title =
      game.type === "game" ? `${homeTeamName} vs ${awayTeamName}` : "Practice";

    const startZdt = Temporal.Instant.fromEpochMilliseconds(
      schedule.schedule_time_s * 1000,
    ).toZonedDateTimeISO(timeZone);

    const lastTime = game.times.at(-1) as
      | { end_time?: number; endTime?: number }
      | undefined;
    const endTimeSec = lastTime?.endTime ?? lastTime?.end_time;
    const rawEndZdt = endTimeSec
      ? Temporal.Instant.fromEpochMilliseconds(
          Number(endTimeSec) * 1000,
        ).toZonedDateTimeISO(timeZone)
      : startZdt.add({ hours: 1 });

    // schedule-x treats events whose start/end fall on different local dates
    // as multi-day, which forces them into the all-day strip. Clamp the end
    // to 23:59 of the start day so they render in the proper time slot; the
    // real end time is still shown via the custom event component below.
    const crossesMidnight =
      Temporal.PlainDate.compare(
        startZdt.toPlainDate(),
        rawEndZdt.toPlainDate(),
      ) !== 0;
    const endZdt = crossesMidnight
      ? startZdt.with({ hour: 23, minute: 59, second: 0, millisecond: 0 })
      : rawEndZdt;

    return [
      {
        id: schedule.id ?? String(i),
        title,
        start: startZdt,
        end: endZdt,
        _schedule: schedule,
      },
    ];
  });
}

function CustomTimeGridEvent({
  calendarEvent,
}: {
  calendarEvent: { title: string; _schedule: Schedule };
}) {
  const { _schedule: schedule } = calendarEvent;
  const game = schedule.merged?.game;

  const isPractice =
    game?.type === "batting_practice" || game?.type === "bullpen";
  const isGame = game?.type === "game";

  const startDate = new Date(schedule.schedule_time_s * 1000);
  const lastTime = game?.times.at(-1) as
    | { end_time?: number; endTime?: number }
    | undefined;
  const endTimeSec = lastTime?.endTime ?? lastTime?.end_time;
  const endDate = endTimeSec ? new Date(Number(endTimeSec) * 1000) : null;

  const formatTime = (d: Date) =>
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  return (
    <div
      className={cn(
        "cursor-pointer",
        "flex flex-col gap-0.5 text-xs h-full rounded p-1",
        isGame &&
          "border-l-3 border-[hsla(129,46%,49%,1)] bg-[rgb(234,247,235)]",
        isPractice &&
          "border-l-3 border-[hsla(230,100%,67%,1)] bg-[rgb(236,239,255)]",
      )}
    >
      <div className="font-semibold truncate">{calendarEvent.title}</div>
      <div className="opacity-70">
        {formatTime(startDate)}
        {endDate ? ` – ${formatTime(endDate)}` : ""}
      </div>
    </div>
  );
}

function CustomEventModal({
  calendarEvent,
  field,
}: {
  calendarEvent: { _schedule: Schedule };
  field: MachinePublicHydrated | undefined;
}) {
  return (
    <UpcomingEventItem
      event={calendarEvent._schedule}
      currentGameUuid={field?.currentGame?.gameUuid}
    />
  );
}

export type CalendarRange = { start: string; end: string };

function getCurrentWeekRange(): CalendarRange {
  const today = new Date();
  const day = today.getDay();
  const start = new Date(today);
  start.setDate(today.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
}

type FieldCalendarProps = {
  field?: MachinePublicHydrated;
};

const FieldCalendar = memo(function FieldCalendar({
  field,
}: FieldCalendarProps) {
  const [range, setRange] = useState<CalendarRange>(getCurrentWeekRange());
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { events } = useCalendarEvents({
    machineUuid: field?.machineUuid,
    range,
    refetchIntervalMs: 30_000,
  });

  const calendar = useCalendarApp({
    views: [createViewWeek()],
    defaultView: "week",
    events: [],
    timezone: timeZone,
    dayBoundaries: {
      start: "06:00",
      end: "23:00",
    },
    plugins: [createEventModalPlugin()],
    callbacks: {
      onRangeUpdate: (calRange) =>
        setRange({
          start: calRange.start.toString(),
          end: calRange.end.toString(),
        }),
    },
  });

  useEffect(() => {
    if (!calendar) return;

    const config = (
      calendar as unknown as {
        $app?: {
          config?: {
            dayBoundaries?: { value: { start: number; end: number } };
          };
        };
      }
    ).$app?.config;
    const signal = config?.dayBoundaries;
    if (signal) {
      const next = computeStretchedBoundaryPoints(events);
      const cur = signal.value;
      if (cur.start !== next.start || cur.end !== next.end) {
        signal.value = next;
      }
    }

    calendar.events.set(toCalendarEvents(events));
  }, [calendar, events]);

  return (
    <div
      className={cn(
        "[&_.sx-react-calendar-wrapper]:w-full [&_.sx-react-calendar-wrapper]:max-w-full",
      )}
    >
      {!field ? (
        <Skeleton className="rounded w-[70vw] h-200 mx-auto" />
      ) : (
        <ScheduleXCalendar
          calendarApp={calendar}
          customComponents={{
            timeGridEvent: CustomTimeGridEvent,
            eventModal: (props) => (
              <CustomEventModal {...props} field={field} />
            ),
          }}
        />
      )}
    </div>
  );
});

export { FieldCalendar };
